import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../../utils/auth';
import { formatUsDateTimeFromIso } from '../../utils/usDateFormat';
import { formatEmployeeNameWithNo } from '../../utils/employeeDisplay';
import { FaBell, FaTimes } from "react-icons/fa";
import { isActionableNotification } from '../../utils/notificationNavigation';

// Utility function to check if user has admin privileges (admin or super role)
const hasAdminPrivileges = (role) => {
  return role === 'admin' || role === 'super';
};

// Notification Center Component with enhanced styling
const NotificationCenter = ({ userProfile }) => {
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [dismissingIds, setDismissingIds] = useState([]);
  const dismissedIdsRef = useRef(new Set());

  useEffect(() => {
    const canReceiveNotifications =
      userProfile?.role === 'manager' ||
      userProfile?.role === 'staff' ||
      hasAdminPrivileges(userProfile?.role);

    if (!canReceiveNotifications) {
      return undefined;
    }

    fetchNotifications();
    const intervalId = setInterval(fetchNotifications, 60000);
    return () => clearInterval(intervalId);
  }, [userProfile]);

  const fetchNotifications = async () => {
    try {
       const headers = await getAuthHeaders();
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/notifications`, {
        headers
      });
      const incoming = response.data.notifications || [];
      const notificationData = incoming.filter(
        (notification) => !dismissedIdsRef.current.has(notification.id)
      );
      const pendingUnreadDismissals = incoming.filter(
        (notification) => dismissedIdsRef.current.has(notification.id) && !notification.is_read
      ).length;
      setNotifications(notificationData);
      setUnreadCount(Math.max(0, (response.data.total_unread || 0) - pendingUnreadDismissals));
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markAsRead = async (notificationId, is_read, { refresh = true } = {}) => {
    try {
      if (is_read) return;
      const headers = await getAuthHeaders();
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/${notificationId}/read`, {}, {
        headers
      });
      if (refresh) {
        fetchNotifications();
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAsReadClick = (event, notification) => {
    event.stopPropagation();
    event.preventDefault();
    if (notification.is_read || dismissingIds.includes(notification.id)) {
      return;
    }

    dismissedIdsRef.current.add(notification.id);
    setDismissingIds((prev) => [...prev, notification.id]);
    setUnreadCount((prev) => Math.max(0, prev - 1));
    markAsRead(notification.id, notification.is_read, { refresh: false });

    window.setTimeout(() => {
      setNotifications((prev) => prev.filter((item) => item.id !== notification.id));
      setDismissingIds((prev) => prev.filter((id) => id !== notification.id));
    }, 320);
  };

  const handleNotificationClick = async (notification) => {
    await markAsRead(notification.id, notification.is_read);
    setShowDropdown(false);

    if (!isActionableNotification(notification)) {
      window.dispatchEvent(new CustomEvent('notification-info', {
        detail: 'This notification cannot be opened.',
      }));
      return;
    }

    window.dispatchEvent(new CustomEvent('notification-navigate', {
      detail: {
        type: notification.type,
        entityId: notification.request_id,
        notificationId: notification.id,
      },
    }));
  };

  if (userProfile?.role !== 'manager' && userProfile?.role !== 'staff' && !hasAdminPrivileges(userProfile?.role)) {
return null;
  }

  return (
    <div className="notification-center">
      <button 
        className={`notification-bell ${unreadCount > 0 ? 'has-unread' : ''}`}
        onClick={() => {
          const nextOpen = !showDropdown;
          setShowDropdown(nextOpen);
          if (nextOpen) {
            fetchNotifications();
          }
        }}
      >
        <FaBell className='text-[1.2rem]'/>
        {unreadCount > 0 && (
          <span className="notification-count">{unreadCount}</span>
        )}
      </button>

      {showDropdown && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>Notifications</h3>
            <button onClick={() => setShowDropdown(false)}>✕</button>
          </div>
          <div className="notification-list">
            {notifications.length > 0 ? (
              notifications.map(notification => (
                <div 
                  key={notification.id} 
                  className={`notification-item ${!notification.is_read ? 'unread' : ''} ${dismissingIds.includes(notification.id) ? 'dismissing' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  {!notification.is_read && (
                    <button
                      type="button"
                      className="notification-mark-read"
                      title="Mark as read"
                      aria-label="Mark notification as read"
                      onClick={(event) => handleMarkAsReadClick(event, notification)}
                    >
                      <FaTimes />
                    </button>
                  )}
                  <h4>{notification.title}</h4>
                  <p>{notification.message}</p>
                  <div className="notification-meta">
                    <span className="notification-employee">
                      Requestor: {formatEmployeeNameWithNo(notification.employee_name, notification.employee_no)}
                    </span>
                    <span className="notification-time">
                      {formatUsDateTimeFromIso(
                        notification.request_applied_date || notification.created_date
                      ) || '—'}
                    </span>
                  </div>
                  <span className={`notification-priority ${notification.priority}`}>
                    {notification.priority}
                  </span>
                </div>
              ))
            ) : (
              <div className="no-notifications">
                <p>No notifications</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;